/**
 * Cornerstone3D Initialization Module
 *
 * This module handles all Cornerstone3D setup. It must complete
 * before any viewport attempts to render.
 */
import { registerLocalGatewayFallback } from '@/lib/localGateway/registerFallback';
import { isAppleTouchDevice, isPhoneSizedScreen } from '@/lib/platform';
import { Enums, cache, init as coreInit, getWebWorkerManager, imageLoadPoolManager, volumeLoader, } from '@cornerstonejs/core';
import { init as dicomImageLoaderInit } from '@cornerstonejs/dicom-image-loader';
import { init as toolsInit } from '@cornerstonejs/tools';
import { subscribeToImageLoadErrors } from './captureViewerError';
import { warmUpDicomCodecs } from './codecWarmup';
import { subscribeToColorFrameCompaction } from './colorFrameCompaction';
import { purgeDataSetCache } from './datasetSeed';
import { subscribeToImageCacheEviction } from './imageCacheEviction';
import { registerMetadataProvider } from './metadataProvider';
import { PREFETCH_WINDOW_SIZE } from './prefetchBudget';
import { registerSubtractionLoader } from './subtraction';
// Initialization state
let isInitialized = false;
let initPromise = null;
// Rendering engine ID - consistent across app
export const RENDERING_ENGINE_ID = 'PRIMARY_RENDERING_ENGINE';
const MiB = 1024 * 1024;
/**
 * Image-cache budgets for iOS/iPadOS, where what matters is not device RAM but
 * the per-tab ceiling at which the system kills the Safari tab (roughly 1.5-3
 * GB for the WHOLE tab: decoded pixels, the ~25% dataset cache on top, the JS
 * heap, codec workers and GPU textures). A 2 GB pixel cache alone let one CT
 * study reach ~1.45 GB of pixels plus datasets before any MPR volume. MPR and
 * fusion volumes are checked against the same number (memoryManager), so a
 * volume that would not fit is refused with "Volume too large" instead of the
 * tab reloading mid-read.
 */
export const APPLE_PHONE_IMAGE_CACHE_BYTES = 512 * MiB;
export const APPLE_TABLET_IMAGE_CACHE_BYTES = 1024 * MiB;
function deviceMemoryGb() {
    return navigator.deviceMemory;
}
/**
 * Which iOS/iPadOS budget applies, or null for everything else. Requires BOTH
 * an iPhone/iPad and no deviceMemory: every iOS browser is WebKit and never
 * reports it, while Chromium always does, so a Chromium with a spoofed Apple UA
 * (device emulation) keeps its desktop budget.
 */
function appleTouchBudget() {
    if (deviceMemoryGb() !== undefined || !isAppleTouchDevice())
        return null;
    return isPhoneSizedScreen() ? 'phone' : 'tablet';
}
/**
 * Recommended image-cache size, in bytes, scaled to the device.
 *
 * A fixed 2 GB cap thrashed on large studies (10k-slice CT) on capable
 * machines while risking OOM on weak ones. `navigator.deviceMemory` reports
 * approximate RAM in GiB (spec-capped at 8). We give the cache ~half of that,
 * clamped to a sane [1.5 GB, 6 GB] range. Cornerstone evicts least-recently-used
 * images automatically once this ceiling is reached, so a larger budget simply
 * means a larger hot working set, not unbounded growth.
 *
 * Safari never reports deviceMemory, which used to mean the 4 GB fallback and
 * a 2 GB cache on every iPhone and iPad. Those return their own, smaller
 * budget BEFORE the 1.5 GB floor (which would otherwise clamp it straight back
 * up). macOS Safari (no touch points) and Chromium (which reports
 * deviceMemory) are unchanged.
 */
export function getRecommendedImageCacheBytes() {
    const apple = appleTouchBudget();
    if (apple)
        return apple === 'phone' ? APPLE_PHONE_IMAGE_CACHE_BYTES : APPLE_TABLET_IMAGE_CACHE_BYTES;
    const cacheSizeGb = Math.min(6, Math.max(1.5, (deviceMemoryGb() ?? 4) * 0.5));
    return cacheSizeGb * 1024 * MiB;
}
/**
 * Fraction of the recommended (decoded-pixel) image cache the wadouri dataset
 * cache may occupy before the prefetcher switches its focused-series transport
 * back to per-object.
 *
 * The dataset cache holds the compressed shadow of an already-bounded set for
 * LOOK-AHEAD series — bounded by the pixel cache's own headroom test — but the
 * focused/visible series deliberately ignores that test (it is what the reader
 * is waiting on), so its bundle bytes are otherwise unbounded. This is a
 * one-way switch, not an eviction policy: over the ceiling, background bundle
 * transport turns off for the rest of the study and the prefetcher falls back
 * to per-object, whose datasets are released with their images by the
 * image-cache eviction policy (./imageCacheEviction). See
 * docs/planning/2026-08-25-bundle-aware-prefetch.md.
 */
export const DATASET_CACHE_HIGH_WATER_FRACTION = 0.25;
/**
 * localStorage key for a per-machine diagnostic override of the dataset-cache
 * high-water mark, in megabytes. Read once at init, overriding the derived
 * `DATASET_CACHE_HIGH_WATER_FRACTION × getRecommendedImageCacheBytes()`
 * number. `0` disables bundle transport in the prefetcher entirely while
 * leaving demand-delivery bundling untouched — this is a diagnosis lever for
 * one machine, not a product setting, which is why it lives in localStorage
 * rather than server-side. There is no server-side switch to reach for either:
 * the only thing that turns bundling off there is removing the signing key,
 * which stops it for everyone at once.
 */
export const DATASET_CACHE_HIGH_WATER_OVERRIDE_KEY = 'newlumen.datasetCacheHighWaterMb';
/**
 * Parse the diagnostic override, in megabytes.
 *
 * `0` is a valid, meaningful override — it is what disables bundle transport
 * — so it must not be treated the same as "not set". Anything else that isn't
 * a finite, non-negative number (missing, blank, garbage, negative) falls back
 * to "not set" so a stray or corrupted value can never turn into `NaN` or a
 * negative ceiling downstream.
 */
export function readDatasetCacheHighWaterOverrideMb() {
    let raw;
    try {
        raw = localStorage.getItem(DATASET_CACHE_HIGH_WATER_OVERRIDE_KEY);
    }
    catch {
        return undefined;
    }
    if (raw === null || raw.trim() === '')
        return undefined;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0)
        return undefined;
    return parsed;
}
/**
 * The dataset-cache high-water mark, in bytes: the diagnostic override when
 * one is set, otherwise {@link DATASET_CACHE_HIGH_WATER_FRACTION} of
 * {@link getRecommendedImageCacheBytes}.
 */
export function getDatasetCacheHighWaterBytes() {
    const overrideMb = readDatasetCacheHighWaterOverrideMb();
    if (overrideMb !== undefined)
        return overrideMb * 1024 * 1024;
    return DATASET_CACHE_HIGH_WATER_FRACTION * getRecommendedImageCacheBytes();
}
/**
 * Decode-worker ceilings on iOS/iPadOS. Every worker holds its own codec WASM
 * heaps inside the same per-tab memory ceiling as the image cache (see
 * APPLE_PHONE_IMAGE_CACHE_BYTES), and Safari reports 8 cores on most models,
 * which would otherwise mean 6 workers.
 */
export const APPLE_PHONE_MAX_WEB_WORKERS = 2;
export const APPLE_TABLET_MAX_WEB_WORKERS = 3;
/**
 * Number of DICOM decode web workers to spawn.
 *
 * J2K/JPEG-LS decode is CPU-bound and parallelizes across workers, so on capable
 * machines the old `min(6, cores-1)` cap left throughput on the table for large
 * CT series (6 was the decode-throughput ceiling). We now raise toward
 * `min(cores-2, 12)` — leaving headroom for the main thread and the render loop —
 * while never dropping below what the old formula would have produced on this
 * machine (so weaker devices never regress) and never below 1. warmUpDicomCodecs
 * amortizes the per-worker codec compile, so a higher worker count is safe.
 * iPhones and iPads are capped lower (APPLE_*_MAX_WEB_WORKERS) for memory.
 */
export function computeMaxWebWorkers() {
    const cores = navigator.hardwareConcurrency || 4;
    const previousFloor = Math.min(6, Math.max(cores - 1, 1));
    const raised = Math.min(12, cores - 2);
    const workers = Math.max(previousFloor, raised, 1);
    const apple = appleTouchBudget();
    if (!apple)
        return workers;
    return Math.min(workers, apple === 'phone' ? APPLE_PHONE_MAX_WEB_WORKERS : APPLE_TABLET_MAX_WEB_WORKERS);
}
/**
 * Raise the decode worker pool's background (Prefetch) concurrency so background
 * image decodes can use (almost) every worker. Cornerstone's
 * CentralizedWorkerManager caps Prefetch-type requests at 5 by default, so with
 * 6+ workers some always sat idle during bulk prefetch. We cap at
 * max(1, workerCount - 1) rather than workerCount so one decode worker always
 * has headroom for Interaction-type decodes (active scrolling) even while a
 * full 24-image prefetch window is saturating the pool — otherwise scroll
 * decodes queue behind background ones. Best-effort: swallow errors (e.g. the
 * worker manager not being ready in some environments) so init never fails
 * over this.
 */
export function configureDecodeWorkerPool(workerCount) {
    try {
        getWebWorkerManager().workerPoolManager.setMaxSimultaneousRequests(Enums.RequestType.Prefetch, Math.max(1, workerCount - 1));
    }
    catch (error) {
        console.warn('[Cornerstone] Could not raise decode worker prefetch concurrency:', error);
    }
}
/**
 * Cap the *image load* request pool's Prefetch concurrency (distinct from the
 * decode worker pool above — this one governs how many HTTP requests
 * streaming MPR's volume loader is allowed to have in flight at once, not
 * decode CPU work). cs3d's imageLoadPoolManager module defaults every
 * request type to 1000 simultaneous requests, so a cold MPR open on a
 * several-hundred-slice CT would otherwise fire every slice's request at
 * once — uncancellable if MPR is closed early, and a cold-CDN burst onto the
 * storage proxy. PREFETCH_WINDOW_SIZE (see prefetchBudget.ts) gives volume
 * loads the same concurrency budget as the app's 2D stack loads. Interaction
 * and Thumbnail are left at their defaults deliberately: viewport stack loads
 * use Interaction and must stay unthrottled. Best-effort, matching
 * configureDecodeWorkerPool: swallow errors so init never fails over this.
 */
export function configureImageLoadPool() {
    try {
        imageLoadPoolManager.setMaxSimultaneousRequests(Enums.RequestType.Prefetch, PREFETCH_WINDOW_SIZE);
    }
    catch (error) {
        console.warn('[Cornerstone] Could not cap image load prefetch concurrency:', error);
    }
}
/**
 * Initialize Cornerstone3D
 * Safe to call multiple times - will only initialize once
 */
export async function initializeCornerstone() {
    if (isInitialized) {
        return;
    }
    if (initPromise) {
        return initPromise;
    }
    initPromise = performInitialization();
    return initPromise;
}
async function performInitialization() {
    try {
        // 1. Initialize core rendering engine
        // preferSizeOverAccuracy: TRUE, decided from a prod demo report ("I can
        // MPR a 500-image series but not a 1000-image series"). With it false and
        // EXT_texture_norm16 unavailable, vtk silently promotes int16 CT data to
        // R32F — 4 bytes/voxel — so a 1000-slice 512^2 CT is a single ~1GB 3D
        // texture whose texStorage3D failure renders as SILENT BLACK. true selects
        // the half-float path (2 bytes/voxel), roughly doubling the slice ceiling.
        // The accuracy cost is ~10 effective bits of window precision, invisible
        // at clinical CT window widths; a black pane is not.
        // (original note: preferSizeOverAccuracy:false keeps high bit-depth volume textures;
        // sampleDistanceMultiplier 1.0 is the global default for 3D ray-sampling quality
        await coreInit({
            rendering: {
                preferSizeOverAccuracy: true,
                volumeRendering: { sampleDistanceMultiplier: 1.0 },
            },
            debug: {},
        });
        console.log('[Cornerstone] Core initialized');
        // 1b. Cap the image-load request pool's Prefetch concurrency so a cold MPR
        // open doesn't fire every slice's request at once (see configureImageLoadPool).
        // Synchronous — safe to run every init, including under vitest.
        configureImageLoadPool();
        // 2. Initialize DICOM image loader with web workers. Scaled to the machine
        // (see computeMaxWebWorkers) — 6 was the J2K decode-throughput ceiling on
        // large CT series; capable machines now get up to 12. Each worker spawns its
        // own decoder script (visible as repeated requests in Network tab).
        const workerCount = computeMaxWebWorkers();
        await dicomImageLoaderInit({
            maxWebWorkers: workerCount,
        });
        console.log('[Cornerstone] DICOM Image Loader initialized');
        // 2b. Pre-compile each worker's WASM codecs (JPEG-LS for what ingest stores;
        // J2K for images already in the archive) so the first image opened
        // doesn't pay the per-worker codec fetch+compile, and raise the decode pool's
        // background (Prefetch) concurrency so bulk prefetch uses all but one worker
        // (the default Prefetch cap is 5; one worker is kept clear for Interaction
        // decodes). Both are skipped under vitest, where there are
        // no real workers. Fire-and-forget warm-up; worker-pool config is synchronous.
        if (process.env.NODE_ENV !== 'test') {
            void warmUpDicomCodecs(workerCount);
            configureDecodeWorkerPool(workerCount);
        }
        // 3. Log available volume loader schemes (for debugging)
        const schemes = volumeLoader.getVolumeLoaderSchemes();
        console.log('[Cornerstone] Available volume loader schemes:', schemes);
        // 4. Initialize tools library
        await toolsInit();
        console.log('[Cornerstone] Tools initialized');
        // 5. Register custom metadata provider for pixel spacing calibration
        registerMetadataProvider();
        // Teaches Cornerstone the `nlsub:` scheme so a contrast subtraction is
        // just another series of images: the stack viewport, MPR, thumbnails
        // and measurements all work on it without knowing what it is.
        registerSubtractionLoader();
        // 6. Forward per-image load failures to Sentry (idempotent subscribe)
        subscribeToImageLoadErrors();
        // 6a. Drop the decode-time copies JPEG-baseline colour frames keep beside
        // their pixels, so the cache size below is measured against what each frame
        // really holds (idempotent subscribe) — see ./colorFrameCompaction.
        subscribeToColorFrameCompaction();
        // 6a'. Evict whole off-screen objects, least recently used first, ahead of
        // any insert that would take the cache over its cap. Cornerstone's own LRU
        // skips nearly everything the wadouri loader files (idempotent subscribe) —
        // see ./imageCacheEviction.
        subscribeToImageCacheEviction();
        // 6b. Redirect local-gateway DICOM requests to the cloud URL once a
        // clinic's gateway origin has been disabled by the session circuit
        // breaker (idempotent subscribe) — see lib/localGateway.
        registerLocalGatewayFallback();
        // 7. Configure cache size, scaled to device memory (see helper). The
        // eviction policy above keeps the cache under this ceiling, so it bounds the
        // working set instead of letting large studies grow the cache without limit.
        cache.setMaxCacheSize(getRecommendedImageCacheBytes());
        isInitialized = true;
        console.log('[Cornerstone] Initialization complete');
    }
    catch (error) {
        initPromise = null;
        console.error('[Cornerstone] Initialization failed:', error);
        throw error;
    }
}
/**
 * Destroy Cornerstone (call on app unmount)
 */
export function destroyCornerstone() {
    cache.purgeCache();
    // purgeCache only frees DECODED images. The wadouri loader keeps the parsed
    // DICOM byte arrays in a cache of its own, normally released by the `decache`
    // hook the fetch path installs — a path bundle-seeded images never take. See
    // ./datasetSeed.
    purgeDataSetCache();
    isInitialized = false;
    initPromise = null;
}
